import {
  CUSTOM_ROLE_EXTRAS,
  MODULE_LEVELS,
  PERMISSIONS,
  ROLE_TEMPLATES,
  levelPermissions,
  type RoleTemplate,
} from "@how/permissions";
import type { ModuleKey } from "@how/types";
import type { CustomRole, Level } from "./team-queries";

/**
 * What the Roles and Team screens say about roles, read from the same registry `pnpm catalog:generate` writes to the
 * database (`pnpm catalog:check` keeps the two in step), so the words here are what `role_permissions` holds.
 */

export const SYSTEM_ROLES: readonly RoleTemplate[] = ROLE_TEMPLATES;

/** A permission's sentence ("View people"), or its key when the registry has none. */
export function permissionLabel(key: string): string {
  return PERMISSIONS.find((p) => p.key === key)?.description ?? key;
}

/** The ten areas a custom role has a level in, in the registry's order, with their names. */
export const AREAS: readonly { readonly key: string; readonly label: string }[] = MODULE_LEVELS.map(
  (a) => ({ key: a.area, label: a.displayName }),
);

export function areaLabel(key: string): string {
  return AREAS.find((a) => a.key === key)?.label ?? key;
}

/**
 * The levels an area offers. An area whose next level adds nothing offers no such level (Check-in has nothing to
 * view; Services and Membership stop at Edit), so a level that would do nothing is never a choice.
 */
export function levelsFor(areaKey: string): readonly Level[] {
  const found = MODULE_LEVELS.find((a) => a.area === areaKey);
  if (found === undefined) return ["none"];
  const out: Level[] = ["none"];
  if (found.view.length > 0) out.push("view");
  if (found.edit.length > 0) out.push("edit");
  if (found.manage.length > 0) out.push("manage");
  return out;
}

export const LEVEL_LABEL: Record<Level, string> = {
  none: "None",
  view: "View",
  edit: "Edit",
  manage: "Manage",
};

/** The cumulative permissions of a level, as sentences. */
export function levelSentences(areaKey: string, level: Level): readonly string[] {
  return levelPermissions(areaKey, level).map(permissionLabel);
}

/** The area that is not a module (communications has no owning module); every other area is its module key. */
export function areaModule(areaKey: string): ModuleKey | null {
  return areaKey === "communications" ? null : (areaKey as ModuleKey);
}

/** The three sensitive extras, each a decision a church makes on purpose (ADR 0054), with the words for its switch. */
export const EXTRAS: readonly {
  readonly key: string;
  readonly label: string;
  readonly description: string;
}[] = CUSTOM_ROLE_EXTRAS.map((key) => {
  switch (key) {
    case "people.read_minors":
      return {
        key,
        label: "See children's records",
        description:
          "Children's names, households and details. Off unless you decide this role needs it, for example a kids' check-in lead.",
      };
    case "notes.read_confidential":
      return {
        key,
        label: "Read confidential notes",
        description:
          "Notes marked confidential on a person's record, which ordinary note readers do not see.",
      };
    default:
      return {
        key,
        label: "Read confidential care",
        description:
          "Care requests and text conversations marked confidential, such as a pastoral matter.",
      };
  }
});

export function extraLabel(key: string): string {
  return EXTRAS.find((e) => e.key === key)?.label ?? key;
}

/** A role's name for a key: a system role's from the registry, a custom role's from the church's list. */
export function roleName(key: string, custom: readonly CustomRole[]): string {
  return (
    SYSTEM_ROLES.find((r) => r.key === key)?.name ?? custom.find((r) => r.key === key)?.name ?? key
  );
}

/** Roles that hand out administration (owner, administrator): granted with care, and never campus-limited. */
export function isPrivilegedRole(key: string): boolean {
  return SYSTEM_ROLES.find((r) => r.key === key)?.isPrivileged ?? false;
}
